import CantileverCard from 'components/CantileverCard'
import { StyledUploadButton } from 'features/assetDatabase/components/assetOverview/assetDetails/imagesSection/UploadImages'
import { useRolodexEdit } from 'features/fde'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoComponent, GeckoInspection, GeckoReport, GeckoUnit } from 'types'

import { ImagesProvider } from './ImagesContext'
import { ImagesRow } from './ImagesRow'
import { ImagesSubSectionContainer } from './ImagesSubSectionContainer'
interface ImagesSectionProps {
  assetThumbnails: readonly GeckoReport[]
  assetThumbnailURLs: readonly string[]
  canUploadFiles?: boolean
  isLoading: boolean
  primaryImageIndex: number | null
  refreshData: () => Promise<void>
  refreshThumbnails: () => Promise<void>
  setPrimaryImageIndex: React.Dispatch<React.SetStateAction<number | null>>
  unit: GeckoUnit | undefined
  unitID: string | undefined
  components: readonly GeckoComponent[]
  inspections: readonly GeckoInspection[]
}

const NoImagesContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.875rem;
  align-self: stretch;
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

const LoadingContainer = styled(NoImagesContainer)`
  // Inherits all styles from NoImagesContainer
`

const ImagesSection: React.FC<ImagesSectionProps> = (props) => {
  const {
    canUploadFiles,
    assetThumbnails,
    assetThumbnailURLs,
    primaryImageIndex,
    refreshThumbnails,
    setPrimaryImageIndex,
    isLoading,
    unit,
    unitID,
    refreshData,
    components,
    inspections,
  } = props
  const { rolodexPatch } = useRolodexEdit()
  const [imageVisibility, setImageVisibility] = useState<boolean[]>([])

  const noVisibleImages = useMemo(
    () => imageVisibility.every((visible) => !visible),
    [imageVisibility]
  )

  const { unitThumbnailUrls, unitThumbnails } = useMemo(() => {
    const filteredItems = assetThumbnails
      .map((thumbnail, index) => ({ thumbnail, index }))
      .filter(
        ({ thumbnail }) =>
          (!thumbnail.data.components ||
            thumbnail.data.components.length === 0) &&
          (!thumbnail.data.inspections ||
            thumbnail.data.inspections.length === 0) &&
          thumbnail.data.file_category === 'image'
      )

    return {
      unitThumbnailUrls: filteredItems.map(
        ({ index }) => assetThumbnailURLs[index]
      ),
      unitThumbnails: filteredItems.map(({ thumbnail }) => thumbnail),
    }
  }, [assetThumbnails, assetThumbnailURLs])

  const hasUnitImages = unitThumbnailUrls.length > 0 && !noVisibleImages

  useEffect(() => {
    setImageVisibility(new Array(assetThumbnailURLs.length).fill(true))
  }, [assetThumbnailURLs.length])

  const savePrimaryImage = useCallback(
    async (index: number) => {
      if (!unit) return

      setPrimaryImageIndex(index)

      await rolodexPatch([
        {
          id: unit.id,
          data: {
            primary_image: unitThumbnails[index].id,
          },
          tags: unit.tags,
        },
      ])

      await refreshThumbnails()
      await refreshData()
    },
    [
      unit,
      unitThumbnails,
      rolodexPatch,
      setPrimaryImageIndex,
      refreshThumbnails,
      refreshData,
    ]
  )

  useEffect(() => {
    if (!unit || assetThumbnails.length === 0 || isLoading) return

    const primaryImageIdx = assetThumbnails.findIndex(
      (thumbnail) => thumbnail.id === unit.data.primary_image
    )

    if (primaryImageIdx >= 0) {
      setPrimaryImageIndex(primaryImageIdx)
    } else {
      savePrimaryImage(0)
    }
  }, [unit, assetThumbnails, isLoading, savePrimaryImage, setPrimaryImageIndex])

  return (
    unit && (
      <CantileverCard>
        <ImagesProvider
          assetThumbnailURLs={assetThumbnailURLs}
          assetThumbnails={assetThumbnails}
          imageVisibility={imageVisibility}
          setImageVisibility={setImageVisibility}
          forceRefresh={refreshThumbnails}
          components={components}
          inspections={inspections}
          canUploadFiles={canUploadFiles}
          unit={unit}>
          <CantileverCard.CardHeader
            leadingContent={'Images'}
            trailingContent={
              canUploadFiles &&
              unitID && <StyledUploadButton isAssetThumbnail={true} text={''} />
            }
          />
          <CantileverCard.Section topPadding>
            <CantileverCard.Group>
              {isLoading ? (
                <LoadingContainer>Loading images...</LoadingContainer>
              ) : hasUnitImages ? (
                <ImagesRow
                  primaryImageIndex={primaryImageIndex}
                  savePrimaryImage={savePrimaryImage}
                  subSectionURLs={unitThumbnailUrls}
                  subSectionThumbnails={unitThumbnails}
                />
              ) : (
                <NoImagesContainer>No Images</NoImagesContainer>
              )}
            </CantileverCard.Group>
          </CantileverCard.Section>
          <>
            <ImagesSubSectionContainer subSectionType={'Component'} />
            <ImagesSubSectionContainer subSectionType={'Inspection'} />
          </>
        </ImagesProvider>
      </CantileverCard>
    )
  )
}

export default ImagesSection
